// Keeps the tire attribute chips in step with the selected size and adds
// a feature + benefit tooltip for every visible chip.
const VARIANT_UPDATE_EVENT = 'variant:update';
const CHIP_SELECTOR = '.tire-chips__chip';
const TOOLTIP_ID = 'tire-chip-tooltip';


// "Why it matters" is written for the chip's own value (Sammy 10/27: "LI 89" must say what 89 carries,
// "WSW" must say white sidewall, "44 PSI" must say what 44 PSI means) - never a generic family line.
const LOAD_INDEX_KG = {70:335,71:345,72:355,73:365,74:375,75:387,76:400,77:412,78:425,79:437,80:450,81:462,82:475,83:487,84:500,85:515,86:530,87:545,88:560,89:580,90:600,91:615,92:630,93:650,94:670,95:690,96:710,97:730,98:750,99:775,100:800,101:825,102:850,103:875,104:900,105:925,106:950,107:975,108:1000,109:1030,110:1060,111:1090,112:1120,113:1150,114:1180,115:1215,116:1250,117:1285,118:1320,119:1360,120:1400,121:1450,122:1500,123:1550,124:1600,125:1650,126:1700,127:1750,128:1800,129:1850,130:1900,131:1950,132:2000,133:2060,134:2120,135:2180,136:2240,137:2300,138:2360,139:2430,140:2500,141:2575,142:2650,143:2725,144:2800,145:2900,146:3000,147:3075,148:3150,149:3250,150:3350,151:3450,152:3550,153:3650,154:3750,155:3875,156:4000,157:4125,158:4250,159:4375,160:4500};
const SPEED_MPH = {A1:3,A2:6,A3:9,A4:12,A5:16,A6:19,A7:22,A8:25,B:31,C:37,D:40,E:43,F:50,G:56,J:62,K:68,L:75,M:81,N:87,P:93,Q:99,R:106,S:112,T:118,U:124,H:130,V:149,W:168,Y:186};
const LOAD_RANGE = {
  SL: 'Standard Load: the normal passenger-car casing, rated for its full load at about 35-36 psi. Fine for most cars and crossovers that do not call for XL.',
  XL: 'Extra Load: a reinforced passenger casing that carries more weight when inflated higher (about 41-42 psi). Needed if your door sticker calls for XL; also common on SUVs and performance cars.',
  B: 'Load Range B (4-ply rated, 35 psi max): the lightest light-truck construction, mostly on older or trailer sizes.',
  C: 'Load Range C (6-ply rated, 50 psi max): a step up in casing strength for light trucks, vans and trailers.',
  D: 'Load Range D (8-ply rated, 65 psi max): a tougher casing for half-ton trucks that tow or carry a load.',
  E: 'Load Range E (10-ply rated, 80 psi max): the heavy-duty standard for 3/4- and 1-ton trucks - more puncture resistance and payload, with a firmer ride.',
  F: 'Load Range F (12-ply rated, 95 psi max): for heavy-duty pickups and commercial vans that run near their weight limits.',
  G: 'Load Range G (14-ply rated, 110 psi max): commercial-grade casing for dually trucks, heavy trailers and box trucks.',
  H: 'Load Range H (16-ply rated, 125 psi max): commercial truck and heavy-trailer construction.',
  J: 'Load Range J (18-ply rated): commercial truck construction for very heavy axle loads.',
  L: 'Load Range L (20-ply rated): commercial/off-road construction for the heaviest loads.'
};
const SIDEWALL = [
  [/^(BSW|BW|BLK|B|BS|SB|BSL|STANDARD|B\/TL|BLT)$/, 'Black sidewall - the plain all-black factory look. No lettering to clean or keep white.'],
  [/^(WSW|WW|WS|WW B\/S)$|^WHITEWALL/, 'White sidewall - a white band on the sidewall for a classic look. It needs regular cleaning to stay bright.'],
  [/^RWL$/, 'Raised white letters - the brand name stands out in solid white on the sidewall, the classic truck and muscle-car look.'],
  [/^OWL$/, 'Outlined white letters - white-outlined brand lettering on the sidewall for a bold truck look. Many can be mounted letters-in if you prefer all black.'],
  [/^ORWL$/, 'Outlined raised white letters - raised, white-outlined lettering for a bold look on the outside of the wheel.'],
  [/^RBL$/, 'Raised black letters - the lettering is raised but stays black, so it looks aggressive without the white.'],
  [/^ORBL$/, 'Outlined raised black letters - raised black lettering with an outline, a subtle aggressive look with no white.'],
  [/^(RRL|RAISED RED LETTERS)$/, 'Raised red letters - red lettering on the sidewall for a standout custom look.'],
  [/^RRBL$/, 'Raised red and black letters - two-tone raised lettering for a custom look.'],
  [/^VSB$/, 'Vertical serrated band - a black sidewall with a ribbed band. Purely the look; it hides scuffs well.'],
  [/^BSW\/OWL$/, 'Reversible sidewall - black on one side and outlined white letters on the other, so you pick the look at mounting.']
];
const TERRAIN = {
  'H/T': 'Highway-terrain: built for pavement - quiet, smooth and long-wearing on trucks and SUVs that rarely leave the road.',
  'A/T': 'All-terrain: a balance of on-road manners and off-road grip, for dirt roads, gravel and the occasional trail.',
  'R/T': 'Rugged-terrain: sits between all-terrain and mud-terrain - more bite off-road than an A/T, quieter on the highway than an M/T.',
  'M/T': 'Mud-terrain: big open tread blocks that clean out in mud and grip rock. Louder and faster-wearing on pavement.',
  'X/T': 'Extreme-terrain: an aggressive hybrid tread for serious off-road use that still has to drive to the trail.'
};
const SEASON = {
  'all-season': 'Gives you year-round convenience in climates that do not require dedicated winter tires.',
  'summer': 'Built for warm, dry and wet roads with a sticky compound for maximum grip. It hardens below about 45°F and is not for snow or ice.',
  'winter': 'A soft compound and dense siping stay flexible below 45°F for grip on snow and ice. Swap it out in warm months - it wears fast in heat.',
  'all-weather': 'Carries the severe-snow (3PMSF) rating but is built to run all year - one set of tires for places with real winters.'
};

function parseFeature(chip) {
  // the block renders title="Name: value — note"; prepareChips moves it into data-tooltip-feature
  const raw = chip.dataset.tooltipFeature || '';
  const [head, note] = raw.split(' — ');
  const i = head.indexOf(': ');
  return { name: i > -1 ? head.slice(0, i) : '', value: i > -1 ? head.slice(i + 2) : head, note: note || '' };
}

const num = (s) => { const m = String(s).replace(/,/g, '').match(/\d+(\.\d+)?/); return m ? parseFloat(m[0]) : null; };
const lb = (kg) => Math.round(kg * 2.20462).toLocaleString('en-US');

function benefitFor(chip) {
  const family = chip.dataset.family || '';
  const { name, value } = parseFeature(chip);
  const feature = `${name} ${value}`.toLowerCase();
  const code = chip.textContent.trim();
  const lc = code.toLowerCase();

  if (feature.includes('3pmsf') || lc.includes('3pmsf')) {
    return 'Meets a severe-snow traction test, giving you more confidence in real winter conditions.';
  }
  if (lc === 'm+s' || lc === 'm&s' || feature.includes('mud + snow')) {
    return 'Uses tread features intended to improve traction in mud and light snow compared with a typical summer tire. It is not a winter-tire rating - look for 3PMSF for that.';
  }
  if (feature.includes('run flat') || lc.includes('run flat')) {
    return 'Can help you keep moving for a limited distance after pressure loss, reducing the chance of being stranded immediately.';
  }

  switch (family) {
    case 'load_index': {
      const parts = String(value || code.replace(/^LI\s*/i, '')).split('/').map((x) => parseInt(x, 10));
      const kg = LOAD_INDEX_KG[parts[0]];
      if (!kg) break;
      let s = `Load index ${parts[0]} means each tire can carry up to ${lb(kg)} lb (${kg} kg) at its maximum pressure - about ${(Math.round(kg * 2.20462) * 4).toLocaleString('en-US')} lb across four tires. Match or beat the load index on your door-jamb sticker.`;
      if (parts[1] && LOAD_INDEX_KG[parts[1]]) {
        s += ` The second number (${parts[1]}) is the rating per tire when run in pairs on a dual-rear-wheel truck: ${lb(LOAD_INDEX_KG[parts[1]])} lb each.`;
      }
      return s;
    }
    case 'max_psi': {
      const p = num(value || code);
      if (!p) break;
      let s = `${p} PSI is the most air this tire is built to hold cold - a safety limit, not the pressure to run. For daily driving use the pressure on your door-jamb sticker; the headroom up to ${p} PSI is what lets the tire carry its full rated load.`;
      if (p >= 65) s += ' A rating this high is light-truck construction made for towing and heavy payloads.';
      else if (p >= 50) s += ' This is typical of Extra Load passenger tires, which carry more weight at higher pressure.';
      else s += ' This is typical of standard-load passenger tires.';
      return s;
    }
    case 'tread_depth': {
      const d = num(value || code);
      if (!d) break;
      let s = `The tire starts with ${d}/32" of tread. Tires are worn out at 2/32", so you get about ${d - 2}/32" of usable tread - deeper tread means more life and more grip in rain, mud and snow.`;
      if (d >= 16) s += ' This is mud-terrain depth: big bite off-road, but more road noise and faster wear on pavement.';
      else if (d >= 13) s += ' This is all-terrain depth, good for dirt, gravel and light trails.';
      else if (d >= 10) s += ' This is typical of all-season and touring tires.';
      else s += ' Shallow tread like this is typical of performance tires - sharper handling, shorter life.';
      return s;
    }
    case 'sidewall': {
      const v = (value || code).toUpperCase().trim();
      const hit = SIDEWALL.find(([re]) => re.test(v));
      if (hit) return hit[1];
      return `Sidewall style "${value || code}" - this is the look of the sidewall only; it does not change how the tire performs.`;
    }
    case 'speed': {
      const letter = code.split(/[–-]/).pop().trim().toUpperCase();
      const mph = SPEED_MPH[letter];
      if (!mph) break;
      return `Speed rating ${letter} means the tire is tested for sustained speeds up to ${mph} mph. Match or beat the rating your vehicle came with - a lower rating can soften handling.`;
    }
    case 'load': {
      const key = code.replace(/^Load Range\s*/i, '').trim().toUpperCase();
      if (LOAD_RANGE[key]) return LOAD_RANGE[key];
      break;
    }
    case 'warranty': {
      const m = num(value) || (num(code) || 0) * 1000;
      if (!m) break;
      return `The manufacturer backs the tread for ${Math.round(m).toLocaleString('en-US')} miles. If it wears out sooner with proper rotation and pressure, you get prorated credit toward replacements.`;
    }
    case 'season': {
      const s = SEASON[(value || code).toLowerCase()];
      if (s) return s;
      break;
    }
    case 'terrain': {
      if (TERRAIN[code.toUpperCase()]) return TERRAIN[code.toUpperCase()];
      break;
    }
    case 'service':
      if (lc === 'trailer') return 'Trailer fitment: stiff sidewalls resist sway under load. For trailers only - not for cars or trucks.';
      if (lc === 'steer') return 'Steer-axle fitment: built for the front steering axle of a commercial truck for even wear and straight tracking.';
      return 'Clarifies the tire\'s sizing and construction standard so you can match the correct fitment.';
    case 'performance':
      return 'Prioritizes steering response and grip, helping the vehicle feel sharper and more controlled.';
    case 'treadwear':
    case 'utqg':
      return 'Helps you gauge expected tread life. Higher treadwear numbers generally indicate slower wear, though they are not a mileage guarantee.';
    case 'traction':
      return 'Helps indicate wet stopping capability within the UTQG traction scale.';
    case 'efficiency':
      return 'Built for electric vehicles: handles the extra weight and instant torque, and is tuned for lower rolling resistance to help protect range.';
    case 'oe':
      return 'Signals an automaker-specific specification, helping preserve the ride and handling characteristics the vehicle was tuned for.';
  }
  // fallback still names the value rather than a generic line
  return value ? `${name || 'This spec'}: ${value}.` : 'Makes the spec easier to compare so you can choose the right tire for your use.';
}

class TireAttributeChips extends HTMLElement {
  #tooltip = null;
  #activeChip = null;

  #onVariantUpdate = (event) => {
    const doc = event.detail?.data?.html;
    if (!doc || typeof doc.querySelector !== 'function') return;

    const blockId = this.dataset.blockId;
    const next =
      (blockId && doc.querySelector(`tire-attribute-chips[data-block-id="${blockId}"]`)) ||
      doc.querySelector('tire-attribute-chips');

    this.#hideTooltip();

    // A size with no attributes renders an empty wrapper, which correctly clears the row.
    if (next) {
      this.innerHTML = next.innerHTML;
      this.#prepareChips();
    }
  };

  #onMouseOver = (event) => {
    const chip = event.target.closest?.(CHIP_SELECTOR);
    if (!chip || !this.contains(chip)) return;
    this.#showTooltip(chip);
  };

  #onMouseOut = (event) => {
    const chip = event.target.closest?.(CHIP_SELECTOR);
    if (!chip || chip !== this.#activeChip) return;
    if (event.relatedTarget && chip.contains(event.relatedTarget)) return;
    this.#hideTooltip();
  };

  #onFocusIn = (event) => {
    const chip = event.target.closest?.(CHIP_SELECTOR);
    if (!chip || !this.contains(chip)) return;
    this.#showTooltip(chip);
  };

  #onFocusOut = (event) => {
    const chip = event.target.closest?.(CHIP_SELECTOR);
    if (!chip || chip !== this.#activeChip) return;
    this.#hideTooltip();
  };

  #onViewportChange = () => {
    if (this.#activeChip && this.#tooltip?.dataset.visible === 'true') {
      this.#positionTooltip(this.#activeChip);
    }
  };

  connectedCallback() {
    this.#ensureTooltip();
    this.#prepareChips();

    document.addEventListener(VARIANT_UPDATE_EVENT, this.#onVariantUpdate);
    this.addEventListener('mouseover', this.#onMouseOver);
    this.addEventListener('mouseout', this.#onMouseOut);
    this.addEventListener('focusin', this.#onFocusIn);
    this.addEventListener('focusout', this.#onFocusOut);
    window.addEventListener('resize', this.#onViewportChange);
    window.addEventListener('scroll', this.#onViewportChange, true);
  }

  disconnectedCallback() {
    document.removeEventListener(VARIANT_UPDATE_EVENT, this.#onVariantUpdate);
    this.removeEventListener('mouseover', this.#onMouseOver);
    this.removeEventListener('mouseout', this.#onMouseOut);
    this.removeEventListener('focusin', this.#onFocusIn);
    this.removeEventListener('focusout', this.#onFocusOut);
    window.removeEventListener('resize', this.#onViewportChange);
    window.removeEventListener('scroll', this.#onViewportChange, true);
    this.#hideTooltip();
  }

  #prepareChips() {
    this.querySelectorAll(CHIP_SELECTOR).forEach((chip) => {
      const nativeTitle = chip.getAttribute('title');
      if (nativeTitle) {
        chip.dataset.tooltipFeature = nativeTitle;
        chip.removeAttribute('title');
      }

      chip.setAttribute('tabindex', '0');
      chip.setAttribute('aria-describedby', TOOLTIP_ID);
    });
  }

  #ensureTooltip() {
    let tooltip = document.getElementById(TOOLTIP_ID);

    if (!tooltip) {
      tooltip = document.createElement('div');
      tooltip.id = TOOLTIP_ID;
      tooltip.setAttribute('role', 'tooltip');
      tooltip.setAttribute('aria-hidden', 'true');
      tooltip.dataset.visible = 'false';
      tooltip.innerHTML = `
        <div class="tire-chip-tooltip__arrow" aria-hidden="true"></div>
        <div class="tire-chip-tooltip__code"></div>
        <div class="tire-chip-tooltip__feature"></div>
        <div class="tire-chip-tooltip__benefit"><strong>Why it matters:</strong> <span></span></div>
      `;

      Object.assign(tooltip.style, {
        position: 'fixed',
        left: '0',
        top: '0',
        width: 'max-content',
        maxWidth: 'min(310px, calc(100vw - 24px))',
        padding: '12px 14px 13px',
        border: '1px solid rgba(255,255,255,0.18)',
        borderRadius: '10px',
        background: 'rgba(10, 10, 12, 0.98)',
        color: '#f5f5f5',
        boxShadow: '0 12px 32px rgba(0,0,0,0.42)',
        fontSize: '12px',
        lineHeight: '1.45',
        letterSpacing: '0',
        textAlign: 'left',
        pointerEvents: 'none',
        opacity: '0',
        visibility: 'hidden',
        transform: 'translateY(-3px)',
        transition: 'opacity 140ms ease, transform 140ms ease, visibility 140ms ease',
        zIndex: '2147483000'
      });

      const style = document.createElement('style');
      style.id = `${TOOLTIP_ID}-styles`;
      style.textContent = `
        #${TOOLTIP_ID}[data-visible="true"] {
          opacity: 1 !important;
          visibility: visible !important;
          transform: translateY(0) !important;
        }
        #${TOOLTIP_ID} .tire-chip-tooltip__code {
          margin: 0 0 5px;
          font-size: 13px;
          font-weight: 800;
          line-height: 1.1;
          letter-spacing: .03em;
        }
        #${TOOLTIP_ID} .tire-chip-tooltip__feature {
          color: rgba(255,255,255,.78);
        }
        #${TOOLTIP_ID} .tire-chip-tooltip__benefit {
          margin-top: 8px;
          padding-top: 8px;
          border-top: 1px solid rgba(255,255,255,.11);
          color: rgba(255,255,255,.94);
        }
        #${TOOLTIP_ID} .tire-chip-tooltip__benefit strong {
          font-weight: 750;
        }
        #${TOOLTIP_ID} .tire-chip-tooltip__arrow {
          position: absolute;
          width: 12px;
          height: 12px;
          background: rgba(10,10,12,.98);
          border-left: 1px solid rgba(255,255,255,.18);
          border-top: 1px solid rgba(255,255,255,.18);
          transform: rotate(45deg);
        }
        #${TOOLTIP_ID}[data-placement="bottom"] .tire-chip-tooltip__arrow {
          top: -7px;
        }
        #${TOOLTIP_ID}[data-placement="top"] .tire-chip-tooltip__arrow {
          bottom: -7px;
          transform: rotate(225deg);
        }
        @media (prefers-reduced-motion: reduce) {
          #${TOOLTIP_ID} { transition: none !important; }
        }
      `;

      document.head.appendChild(style);
      document.body.appendChild(tooltip);
    }

    this.#tooltip = tooltip;
  }

  #showTooltip(chip) {
    this.#ensureTooltip();

    const feature = chip.dataset.tooltipFeature || chip.textContent.trim();
    const code = chip.textContent.trim();
    const benefit = benefitFor(chip);
    const chipColor = getComputedStyle(chip).color;

    this.#tooltip.querySelector('.tire-chip-tooltip__code').textContent = code;
    this.#tooltip.querySelector('.tire-chip-tooltip__code').style.color = chipColor;
    this.#tooltip.querySelector('.tire-chip-tooltip__feature').textContent = feature;
    this.#tooltip.querySelector('.tire-chip-tooltip__benefit span').textContent = benefit;

    this.#activeChip = chip;
    this.#tooltip.dataset.visible = 'true';
    this.#tooltip.setAttribute('aria-hidden', 'false');

    requestAnimationFrame(() => this.#positionTooltip(chip));
  }

  #hideTooltip() {
    if (!this.#tooltip) return;
    this.#tooltip.dataset.visible = 'false';
    this.#tooltip.setAttribute('aria-hidden', 'true');
    this.#activeChip = null;
  }

  #positionTooltip(chip) {
    if (!this.#tooltip || !chip.isConnected) return;

    const gap = 12;
    const chipRect = chip.getBoundingClientRect();
    const tooltipRect = this.#tooltip.getBoundingClientRect();
    const viewportPadding = 12;

    const roomBelow = window.innerHeight - chipRect.bottom;
    const roomAbove = chipRect.top;
    const placeBelow = roomBelow >= tooltipRect.height + gap || roomBelow >= roomAbove;

    let top = placeBelow
      ? chipRect.bottom + gap
      : chipRect.top - tooltipRect.height - gap;

    let left = chipRect.left + chipRect.width / 2 - tooltipRect.width / 2;
    left = Math.max(viewportPadding, Math.min(left, window.innerWidth - tooltipRect.width - viewportPadding));
    top = Math.max(viewportPadding, Math.min(top, window.innerHeight - tooltipRect.height - viewportPadding));

    this.#tooltip.dataset.placement = placeBelow ? 'bottom' : 'top';
    this.#tooltip.style.left = `${Math.round(left)}px`;
    this.#tooltip.style.top = `${Math.round(top)}px`;

    const arrow = this.#tooltip.querySelector('.tire-chip-tooltip__arrow');
    const chipCenter = chipRect.left + chipRect.width / 2;
    const arrowLeft = Math.max(10, Math.min(chipCenter - left - 6, tooltipRect.width - 22));
    arrow.style.left = `${Math.round(arrowLeft)}px`;
  }
}

if (!customElements.get('tire-attribute-chips')) {
  customElements.define('tire-attribute-chips', TireAttributeChips);
}
